import React, { useState, useEffect } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getBizPage, getBizPageFeed, publicBook } from './businessApi';

const CATEGORY_ICONS = {
  wellness:  '💚',
  fitness:   '💪',
  coaching:  '🧠',
  nutrition: '🥗',
  other:     '⭐',
};

const DAY_KEYS   = ['mon','tue','wed','thu','fri','sat','sun'];
const DAYS_EN    = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const DAYS_NL    = ['Maandag','Dinsdag','Woensdag','Donderdag','Vrijdag','Zaterdag','Zondag'];

const EMPTY_BOOK = { booker_name: '', booker_email: '', booker_phone: '', requested_at: '', notes: '' };

const PAGE_TABS = [
  { id: 'overview', nl: 'Overzicht', en: 'Overview' },
  { id: 'services', nl: 'Diensten',  en: 'Services' },
  { id: 'news',     nl: 'Nieuws',    en: 'News' },
  { id: 'info',     nl: 'Info',      en: 'Info' },
];

function TabBar({ tabs, active, onSelect, accentColor, nl }) {
  return (
    <div style={{ display: 'flex', background: _T.bg2, borderBottom: `1px solid ${_T.cardBorderBot}`, position: 'sticky', top: 0, zIndex: 20 }}>
      {tabs.map(t => {
        const isActive = active === t.id;
        return (
          <button key={t.id} onClick={() => onSelect(t.id)}
            style={{ flex: 1, background: 'none', border: 'none', borderBottom: `2px solid ${isActive ? accentColor : 'transparent'}`, cursor: 'pointer', padding: '13px 4px', color: isActive ? accentColor : C.silver, fontSize: 12, fontWeight: isActive ? 700 : 400, transition: 'color 0.15s, border-color 0.15s' }}>
            {nl ? t.nl : t.en}
          </button>
        );
      })}
    </div>
  );
}

export default function BusinessPublicPage({ businessId, lang, onBack }) {
  const nl = lang === 'nl';
  const [biz, setBiz]                 = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [pageTab, setPageTab]         = useState('overview');
  const [selectedSvc, setSelectedSvc] = useState(null);
  const [form, setForm]               = useState(EMPTY_BOOK);
  const [booking, setBooking]         = useState(false);
  const [booked, setBooked]           = useState(false);
  const [bookErr, setBookErr]         = useState(null);
  const [feed, setFeed]               = useState([]);

  useEffect(() => {
    getBizPage(businessId)
      .then(d => { if (d.error) setError(d.error); else setBiz(d); setLoading(false); })
      .catch(() => { setError('Failed to load'); setLoading(false); });
    getBizPageFeed(businessId).then(d => { if (Array.isArray(d)) setFeed(d); });
  }, [businessId]);

  const handleBook = async () => {
    if (!form.booker_name.trim() || !form.booker_email.trim()) {
      setBookErr(nl ? 'Vul naam en e-mail in' : 'Name and email are required');
      return;
    }
    setBooking(true); setBookErr(null);
    const res = await publicBook(businessId, { service_id: selectedSvc.id, ...form }).catch(() => null);
    setBooking(false);
    if (res && res.ok) { setBooked(true); }
    else { setBookErr((res && res.error) || (nl ? 'Boeking mislukt' : 'Booking failed')); }
  };

  const BackBtn = ({ style }) => onBack ? (
    <button onClick={onBack} style={{ background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: C.silver2, fontSize: 18, backdropFilter: 'blur(8px)', flexShrink: 0, ...style }}>‹</button>
  ) : null;

  if (loading) return (
    <div style={{ minHeight: '100vh', background: _T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{CSS}</style>
      {onBack && <div style={{ position: 'absolute', top: 14, left: 14 }}><BackBtn /></div>}
      <div style={{ color: C.silver, fontSize: 14 }}>...</div>
    </div>
  );

  if (error || !biz) return (
    <div style={{ minHeight: '100vh', background: _T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{CSS}</style>
      {onBack && <div style={{ position: 'absolute', top: 14, left: 14 }}><BackBtn /></div>}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🏢</div>
        <div style={{ color: C.silver, fontSize: 14 }}>{nl ? 'Bedrijf niet gevonden' : 'Business not found'}</div>
      </div>
    </div>
  );

  // Booking overlay
  if (selectedSvc) return (
    <div style={{ minHeight: '100vh', background: _T.bg, display: 'flex', flexDirection: 'column' }}>
      <style>{CSS}</style>
      <div style={{ background: _T.bg2, borderBottom: `1px solid ${_T.cardBorderTop}`, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <button onClick={() => { setSelectedSvc(null); setBooked(false); setBookErr(null); }}
          style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.silver2, fontSize: 18, width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }}>‹</button>
        <span style={{ color: C.white, fontWeight: 700, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedSvc.name}
        </span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 16px 40px' }}>
        {booked ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
            <div style={{ color: C.white, fontSize: 20, fontWeight: 700, fontFamily: 'Georgia,serif', marginBottom: 10 }}>
              {nl ? 'Aanvraag verstuurd!' : 'Request sent!'}
            </div>
            <div style={{ color: C.silver, fontSize: 13, lineHeight: 1.7, maxWidth: 320, margin: '0 auto' }}>
              {nl ? `${biz.name} neemt zo snel mogelijk contact met je op.` : `${biz.name} will contact you as soon as possible.`}
            </div>
          </div>
        ) : (
          <>
            <div style={{ background: `${_T.accent}10`, border: `1px solid ${_T.accent}33`, borderRadius: 16, padding: 16, marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ color: C.white, fontWeight: 700, fontSize: 15 }}>
                  {CATEGORY_ICONS[selectedSvc.category] || '⭐'} {selectedSvc.name}
                </span>
                <span style={{ color: _T.accent, fontWeight: 700, fontSize: 15 }}>
                  {selectedSvc.price ? `€${selectedSvc.price}` : (nl ? 'Gratis' : 'Free')}
                </span>
              </div>
              {selectedSvc.description && <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.5, marginBottom: 4 }}>{selectedSvc.description}</div>}
              {selectedSvc.duration_min && <div style={{ color: C.silver2, fontSize: 11 }}>⏱ {selectedSvc.duration_min} min</div>}
            </div>

            <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 14 }}>
              {nl ? 'JOUW GEGEVENS' : 'YOUR DETAILS'}
            </div>

            {[
              { key: 'booker_name',  label: nl ? 'Naam *' : 'Name *', type: 'text' },
              { key: 'booker_email', label: nl ? 'E-mail *' : 'Email *', type: 'email' },
              { key: 'booker_phone', label: nl ? 'Telefoon' : 'Phone', type: 'tel' },
              { key: 'requested_at', label: nl ? 'Gewenste datum/tijd' : 'Preferred date/time', type: 'datetime-local' },
            ].map(({ key, label, type }) => (
              <div key={key} style={{ marginBottom: 12 }}>
                <label style={{ color: C.silver, fontSize: 11, display: 'block', marginBottom: 4 }}>{label}</label>
                <input type={type} value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box', background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '11px 12px', outline: 'none' }} />
              </div>
            ))}

            <div style={{ marginBottom: 20 }}>
              <label style={{ color: C.silver, fontSize: 11, display: 'block', marginBottom: 4 }}>{nl ? 'Opmerkingen' : 'Notes'}</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3}
                style={{ width: '100%', boxSizing: 'border-box', background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '11px 12px', outline: 'none', resize: 'none' }} />
            </div>

            {bookErr && <div style={{ color: '#C05050', fontSize: 12, marginBottom: 12 }}>{bookErr}</div>}

            <button onClick={handleBook} disabled={booking}
              style={{ width: '100%', background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 15, fontWeight: 700, padding: '15px', cursor: 'pointer', opacity: booking ? 0.7 : 1 }}>
              {booking ? '...' : (nl ? '📅 Afspraak aanvragen' : '📅 Request appointment')}
            </button>
          </>
        )}
      </div>
    </div>
  );

  // ── Main public page ──────────────────────────────────────────────
  const hours     = biz.opening_hours || {};
  const dayLabels = nl ? DAYS_NL : DAYS_EN;
  const hasHours  = DAY_KEYS.some(k => hours[k]);
  const hasServices = biz.services && biz.services.length > 0;

  // ── Tabs content ──────────────────────────────────────────────────

  const overviewTab = (
    <div style={{ padding: '20px 16px 40px' }}>
      {/* Description */}
      {biz.description && (
        <div style={{ background: _T.bg3, borderRadius: 16, padding: '16px 18px', border: `1px solid ${_T.cardBorderBot}`, marginBottom: 16 }}>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 8 }}>
            {nl ? 'OVER ONS' : 'ABOUT US'}
          </div>
          <div style={{ color: C.silver2, fontSize: 13, lineHeight: 1.75 }}>{biz.description}</div>
        </div>
      )}

      {/* Quick stats strip */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        {hasServices && (
          <div style={{ flex: 1, background: `${_T.accent}0d`, border: `1px solid ${_T.accent}22`, borderRadius: 14, padding: '14px 12px', textAlign: 'center', cursor: 'pointer' }} onClick={() => setPageTab('services')}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>📋</div>
            <div style={{ fontSize: 16, color: _T.accent, fontWeight: 700 }}>{biz.services.length}</div>
            <div style={{ fontSize: 10, color: C.silver, marginTop: 2 }}>{nl ? 'Diensten' : 'Services'}</div>
          </div>
        )}
        {feed.length > 0 && (
          <div style={{ flex: 1, background: `${_T.accent}0d`, border: `1px solid ${_T.accent}22`, borderRadius: 14, padding: '14px 12px', textAlign: 'center', cursor: 'pointer' }} onClick={() => setPageTab('news')}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>📢</div>
            <div style={{ fontSize: 16, color: _T.accent, fontWeight: 700 }}>{feed.length}</div>
            <div style={{ fontSize: 10, color: C.silver, marginTop: 2 }}>{nl ? 'Updates' : 'Updates'}</div>
          </div>
        )}
        {hasHours && (
          <div style={{ flex: 1, background: `${_T.accent}0d`, border: `1px solid ${_T.accent}22`, borderRadius: 14, padding: '14px 12px', textAlign: 'center', cursor: 'pointer' }} onClick={() => setPageTab('info')}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>🕐</div>
            <div style={{ fontSize: 11, color: _T.accent, fontWeight: 700 }}>{nl ? 'Open' : 'Hours'}</div>
            <div style={{ fontSize: 10, color: C.silver, marginTop: 2 }}>{nl ? 'Tijden' : 'Info'}</div>
          </div>
        )}
      </div>

      {/* Featured services preview (max 2) */}
      {hasServices && (
        <>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>
            {nl ? 'UITGELICHTE DIENSTEN' : 'FEATURED SERVICES'}
          </div>
          {biz.services.slice(0, 2).map(s => (
            <div key={s.id} style={{ background: _T.bg3, borderRadius: 14, padding: '14px 16px', border: `1px solid ${_T.cardBorderBot}`, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 26, lineHeight: 1 }}>{CATEGORY_ICONS[s.category] || '⭐'}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.white, fontWeight: 700, fontSize: 14 }}>{s.name}</div>
                {s.price ? <div style={{ color: '#4AC87A', fontSize: 12, fontWeight: 700, marginTop: 2 }}>€{s.price}</div>
                         : <div style={{ color: '#4AC87A', fontSize: 11, marginTop: 2 }}>{nl ? 'Gratis' : 'Free'}</div>}
              </div>
              <button onClick={() => { setSelectedSvc(s); setBooked(false); setBookErr(null); setForm(EMPTY_BOOK); }}
                style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 10, color: '#0a1220', fontSize: 12, fontWeight: 700, padding: '9px 14px', cursor: 'pointer', flexShrink: 0 }}>
                {nl ? 'Boeken' : 'Book'}
              </button>
            </div>
          ))}
          {biz.services.length > 2 && (
            <button onClick={() => setPageTab('services')}
              style={{ width: '100%', background: 'none', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, color: _T.accent, fontSize: 12, fontWeight: 600, padding: '11px', cursor: 'pointer', marginBottom: 16 }}>
              {nl ? `Alle ${biz.services.length} diensten bekijken →` : `View all ${biz.services.length} services →`}
            </button>
          )}
        </>
      )}

      {/* Latest news preview */}
      {feed.length > 0 && (
        <>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12, marginTop: 8 }}>
            {nl ? 'LAATSTE UPDATE' : 'LATEST UPDATE'}
          </div>
          <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, border: `1px solid ${_T.cardBorderBot}`, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              {feed[0].user?.picture
                ? <img src={feed[0].user.picture} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }} />
                : <div style={{ width: 30, height: 30, borderRadius: '50%', background: feed[0].user?.color || _T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#000', fontWeight: 700, flexShrink: 0 }}>{(feed[0].user?.name || biz.name)[0]}</div>
              }
              <div style={{ color: C.silver, fontSize: 11 }}>{new Date(feed[0].time).toLocaleDateString(nl ? 'nl-NL' : 'en-US')}</div>
            </div>
            {feed[0].img && <img src={feed[0].img} alt="" style={{ width: '100%', borderRadius: 10, marginBottom: 8, objectFit: 'cover', maxHeight: 200 }} />}
            {feed[0].text && <div style={{ color: C.white, fontSize: 13, lineHeight: 1.6 }}>{feed[0].text.length > 180 ? feed[0].text.slice(0, 180) + '…' : feed[0].text}</div>}
          </div>
          {feed.length > 1 && (
            <button onClick={() => setPageTab('news')}
              style={{ width: '100%', background: 'none', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, color: _T.accent, fontSize: 12, fontWeight: 600, padding: '11px', cursor: 'pointer', marginBottom: 16 }}>
              {nl ? `Alle ${feed.length} updates bekijken →` : `View all ${feed.length} updates →`}
            </button>
          )}
        </>
      )}

      {!biz.description && !hasServices && feed.length === 0 && !hasHours && (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: C.silver2, fontSize: 13 }}>
          {nl ? 'Nog geen informatie beschikbaar.' : 'No information available yet.'}
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: 32, color: C.silver2, fontSize: 10 }}>
        Powered by <span style={{ color: _T.accent, fontWeight: 700 }}>SenseLifting</span>
      </div>
    </div>
  );

  const servicesTab = (
    <div style={{ padding: '20px 16px 40px' }}>
      {hasServices ? (
        <>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 14 }}>
            {nl ? 'DIENSTEN & AFSPRAKEN' : 'SERVICES & BOOKINGS'}
          </div>
          {biz.services.map(s => (
            <div key={s.id} style={{ background: _T.bg3, borderRadius: 16, padding: 16, border: `1px solid ${_T.cardBorderBot}`, marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ fontSize: 28, flexShrink: 0, lineHeight: 1, marginTop: 2 }}>{CATEGORY_ICONS[s.category] || '⭐'}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.white, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{s.name}</div>
                {s.description && <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.5, marginBottom: 6 }}>{s.description}</div>}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  {s.duration_min && <span style={{ color: C.silver2, fontSize: 11 }}>⏱ {s.duration_min}m</span>}
                  {s.price
                    ? <span style={{ color: '#4AC87A', fontSize: 12, fontWeight: 700 }}>€{s.price} {s.currency || ''}</span>
                    : <span style={{ color: '#4AC87A', fontSize: 11, fontWeight: 600 }}>{nl ? 'Gratis' : 'Free'}</span>
                  }
                </div>
              </div>
              <button onClick={() => { setSelectedSvc(s); setBooked(false); setBookErr(null); setForm(EMPTY_BOOK); }}
                style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 10, color: '#0a1220', fontSize: 12, fontWeight: 700, padding: '10px 16px', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap', marginTop: 2 }}>
                {nl ? 'Boeken' : 'Book'}
              </button>
            </div>
          ))}
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '56px 20px', color: C.silver2, fontSize: 13 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          {nl ? 'Geen diensten beschikbaar.' : 'No services available.'}
        </div>
      )}
    </div>
  );

  const newsTab = (
    <div style={{ padding: '20px 16px 40px' }}>
      {feed.length > 0 ? (
        <>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 14 }}>
            📢 {nl ? 'NIEUWS & UPDATES' : 'NEWS & UPDATES'}
          </div>
          {feed.map(post => (
            <div key={post.id} style={{ background: _T.bg3, borderRadius: 16, padding: 16, border: `1px solid ${_T.cardBorderBot}`, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                {post.user?.picture
                  ? <img src={post.user.picture} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${_T.cardBorderBot}` }} />
                  : <div style={{ width: 34, height: 34, borderRadius: '50%', background: post.user?.color || _T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#000', fontWeight: 700, flexShrink: 0 }}>{(post.user?.name || biz.name)[0]}</div>
                }
                <div>
                  <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{post.user?.name || biz.name}</div>
                  <div style={{ color: C.silver, fontSize: 11 }}>{new Date(post.time).toLocaleDateString(nl ? 'nl-NL' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                </div>
              </div>
              {post.img && <img src={post.img} alt="" style={{ width: '100%', borderRadius: 12, marginBottom: 10, objectFit: 'cover', maxHeight: 280, border: `1px solid ${_T.cardBorderBot}` }} />}
              {post.text && <div style={{ color: C.white, fontSize: 14, lineHeight: 1.65 }}>{post.text}</div>}
              {post.likes > 0 && <div style={{ color: C.silver, fontSize: 12, marginTop: 10 }}>❤️ {post.likes}</div>}
            </div>
          ))}
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '56px 20px', color: C.silver2, fontSize: 13 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📢</div>
          {nl ? 'Nog geen updates.' : 'No updates yet.'}
        </div>
      )}
    </div>
  );

  const infoTab = (
    <div style={{ padding: '20px 16px 40px' }}>
      {/* Contact info */}
      {(biz.address || biz.phone || biz.email || biz.website) && (
        <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, border: `1px solid ${_T.cardBorderBot}`, marginBottom: 16 }}>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>
            📬 {nl ? 'CONTACT' : 'CONTACT'}
          </div>
          {biz.address && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', paddingBottom: 10, borderBottom: `1px solid ${_T.cardBorderBot}44`, marginBottom: 10 }}>
              <span style={{ color: C.silver, fontSize: 14, flexShrink: 0 }}>📍</span>
              <span style={{ color: C.silver2, fontSize: 13, lineHeight: 1.5 }}>{biz.address}</span>
            </div>
          )}
          {biz.phone && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', paddingBottom: 10, borderBottom: `1px solid ${_T.cardBorderBot}44`, marginBottom: 10 }}>
              <span style={{ color: C.silver, fontSize: 14, flexShrink: 0 }}>📞</span>
              <a href={`tel:${biz.phone}`} style={{ color: _T.accent, fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>{biz.phone}</a>
            </div>
          )}
          {biz.email && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', paddingBottom: 10, borderBottom: `1px solid ${_T.cardBorderBot}44`, marginBottom: 10 }}>
              <span style={{ color: C.silver, fontSize: 14, flexShrink: 0 }}>✉️</span>
              <a href={`mailto:${biz.email}`} style={{ color: _T.accent, fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>{biz.email}</a>
            </div>
          )}
          {biz.website && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ color: C.silver, fontSize: 14, flexShrink: 0 }}>🌐</span>
              <a href={biz.website} target="_blank" rel="noopener noreferrer" style={{ color: _T.accent, fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>{biz.website.replace(/^https?:\/\//, '')}</a>
            </div>
          )}
        </div>
      )}

      {/* Opening hours */}
      {hasHours ? (
        <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, border: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 14 }}>
            🕐 {nl ? 'OPENINGSTIJDEN' : 'OPENING HOURS'}
          </div>
          {DAY_KEYS.map((k, i) => {
            const today = ['sun','mon','tue','wed','thu','fri','sat'][new Date().getDay()];
            const isToday = k === today;
            return hours[k] ? (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: i < DAY_KEYS.length - 1 ? `1px solid ${_T.cardBorderBot}44` : 'none', background: isToday ? `${_T.accent}08` : 'none', borderRadius: isToday ? 8 : 0, paddingLeft: isToday ? 8 : 0, paddingRight: isToday ? 8 : 0 }}>
                <span style={{ color: isToday ? _T.accent : C.silver, fontSize: 12, fontWeight: isToday ? 700 : 400 }}>{dayLabels[i]}</span>
                <span style={{ color: isToday ? C.white : C.white, fontSize: 12, fontWeight: isToday ? 700 : 600 }}>{hours[k]}</span>
              </div>
            ) : null;
          })}
        </div>
      ) : (
        !biz.address && !biz.phone && !biz.email && !biz.website && (
          <div style={{ textAlign: 'center', padding: '56px 20px', color: C.silver2, fontSize: 13 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>ℹ️</div>
            {nl ? 'Geen contactinformatie beschikbaar.' : 'No contact information available.'}
          </div>
        )
      )}
    </div>
  );

  const accentColor = _T.accent;

  return (
    <div style={{ minHeight: '100vh', background: _T.bg, display: 'flex', flexDirection: 'column' }}>
      <style>{CSS}</style>

      {/* ── Hero ── */}
      <div style={{ background: `linear-gradient(160deg,${_T.bg2} 0%,${_T.bg3} 100%)`, padding: '44px 20px 32px', borderBottom: `1px solid ${_T.cardBorderTop}`, textAlign: 'center', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 50% 80%,${accentColor}18,transparent 70%)` }} />
        {onBack && (
          <button onClick={onBack} style={{ position: 'absolute', top: 14, left: 14, zIndex: 10, background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: C.silver2, fontSize: 18, backdropFilter: 'blur(8px)' }}>‹</button>
        )}
        <div style={{ position: 'relative' }}>
          {biz.logo_url
            ? <img src={biz.logo_url} alt={biz.name} style={{ width: 84, height: 84, borderRadius: 20, objectFit: 'cover', marginBottom: 14, border: `3px solid ${accentColor}44`, boxShadow: `0 8px 32px ${accentColor}20` }} />
            : <div style={{ width: 84, height: 84, borderRadius: 20, background: `${accentColor}20`, border: `3px solid ${accentColor}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, margin: '0 auto 14px' }}>🏢</div>
          }
          <div style={{ color: C.white, fontSize: 24, fontWeight: 700, fontFamily: 'Georgia,serif', marginBottom: 5 }}>{biz.name}</div>
          {biz.industry && <div style={{ color: accentColor, fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: 'uppercase', marginBottom: 6 }}>{biz.industry}</div>}
          {biz.address && <div style={{ color: C.silver, fontSize: 12 }}>📍 {biz.address}</div>}
        </div>
      </div>

      {/* ── Tab bar ── */}
      <TabBar tabs={PAGE_TABS} active={pageTab} onSelect={setPageTab} accentColor={accentColor} nl={nl} />

      {/* ── Scrollable content ── */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {pageTab === 'overview' && overviewTab}
        {pageTab === 'services' && servicesTab}
        {pageTab === 'news'     && newsTab}
        {pageTab === 'info'     && infoTab}
      </div>
    </div>
  );
}
